import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { firstPortalPath, usePortalAccess } from '../../components/PortalAccess';
import { setMainButton, hideMainButton, isTelegram } from '../../utils/telegram';

const STATUS_LABELS = { new: 'Новая', in_progress: 'В работе', closed: 'Закрыта' };
const PRIORITY_EMOJI = { urgent: '🔴', normal: '🟡', low: '🟢' };
const BLOCK_NAMES = {
  tech: '💻 Тех. работа',
  design: '🎨 Дизайн',
  video: '🎬 Видео',
  loyalty: '🎁 Программа лояльности',
  social: '📱 Социальные сети',
  novelties: '🆕 Новинки',
};

const PORTAL_KEYS = ['helpdesk.dashboard', 'helpdesk.tickets', 'helpdesk.users', 'helpdesk.networks', 'knowledge.manage', 'inventory.manage', 'signage.manage', 'smm.settings'];

export default function Home() {
  const navigate = useNavigate();
  const { permissions } = usePortalAccess();
  const [tickets, setTickets] = useState([]);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('open');

  useEffect(() => {
    api.get('/api/app/me').then(async meRes => {
      setUser(meRes.data);
      if (meRes.data.permissions?.['helpdesk.my_tickets']) {
        const ticketsRes = await api.get('/api/app/tickets');
        setTickets(ticketsRes.data);
      }
    }).catch(() => setError('Ошибка загрузки')).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!isTelegram() || !permissions['helpdesk.create']) return undefined;
    const create = () => navigate('/app/create');
    setMainButton('Создать заявку', create);
    return () => hideMainButton();
  }, [navigate, permissions]);

  const handleOpenSMM = async () => {
    try {
      const res = await api.get('/api/app/smm-token');
      localStorage.setItem('smm_token', res.data.access_token);
      const keys = [['smm.overview', 'dashboard'], ['smm.vk', 'vk'], ['smm.telegram', 'telegram'], ['smm.instagram', 'instagram'], ['smm.max', 'max']];
      const path = keys.find(([key]) => permissions[key])?.[1];
      window.location.href = `/smm/${path || 'dashboard'}`;
    } catch {
      setError('Нет доступа к SMM');
    }
  };

  const openPortal = async path => {
    try {
      const response = await api.get('/api/app/portal-token');
      localStorage.removeItem('admin_token');
      localStorage.setItem('portal_token', response.data.access_token);
      navigate(path || firstPortalPath(response.data.permissions));
    } catch { setError('Не удалось открыть раздел портала'); }
  };

  if (loading) return <div className="app-loader"><div className="spinner" /><p>Загрузка...</p></div>;

  const filteredTickets = tickets.filter(ticket => {
    if (statusFilter === 'all') return true;
    if (statusFilter === 'open') return ticket.status !== 'closed';
    return ticket.status === 'closed';
  });

  return (
    <div className="app-page">
      <div className="home-hero">
        <div>
          <div className="home-eyebrow">Helpdesk</div>
          <h1>{permissions['helpdesk.my_tickets'] ? 'Мои заявки' : 'Портал'}</h1>
          {user && <p>Здравствуйте, {user.first_name}</p>}
        </div>
        {permissions['helpdesk.my_tickets'] && <div className="home-counter" aria-label={`Всего заявок: ${tickets.length}`}>
          <strong>{tickets.length}</strong>
          <span>всего</span>
        </div>}
      </div>

      <main className="home-content">
        {error && <div className="error-msg">{error}</div>}

        {permissions['helpdesk.my_tickets'] && <div className="ticket-segments" role="tablist" aria-label="Статус заявок">
          {[['open', 'Открытые'], ['closed', 'Закрытые'], ['all', 'Все']].map(([value, label]) => (
            <button key={value} type="button" className={`ticket-segment ${statusFilter === value ? 'active' : ''}`} onClick={() => setStatusFilter(value)}>{label}</button>
          ))}
        </div>}

        <div className="home-actions">
          {!isTelegram() && permissions['helpdesk.create'] && (
            <button className="btn-primary home-create-action" onClick={() => navigate('/app/create')}>
              <span>+</span> Создать заявку
            </button>
          )}
        {permissions['knowledge.read'] && (
          <button className="home-smm-action home-knowledge-action" onClick={() => navigate('/app/knowledge')}>
            <span className="home-smm-icon home-knowledge-icon">📚</span>
            <span><strong>База знаний</strong><small>Инструкции по технике и сервисам</small></span>
            <span className="home-action-arrow">›</span>
          </button>
        )}
        {permissions['academy.learn'] && (
          <button className="home-smm-action home-academy-action" onClick={() => navigate('/app/academy')}>
            <span className="home-smm-icon home-academy-icon">🎓</span>
            <span><strong>Академия</strong><small>Обучение, уроки и тесты</small></span>
            <span className="home-action-arrow">›</span>
          </button>
        )}
        {user && ['smm.overview', 'smm.vk', 'smm.telegram', 'smm.instagram', 'smm.max'].some(key => permissions[key]) && (
          <button className="home-smm-action" onClick={handleOpenSMM}>
            <span className="home-smm-icon">↗</span>
            <span><strong>SMM-аналитика</strong><small>Статистика площадок</small></span>
            <span className="home-action-arrow">›</span>
          </button>
        )}
        {permissions['content_plan.manage'] && <button className="home-smm-action" onClick={() => openPortal('/admin/content-plan')}>
          <span className="home-smm-icon">🗓</span><span><strong>Контент-план</strong><small>Планирование и материалы</small></span><span className="home-action-arrow">›</span>
        </button>}
        {PORTAL_KEYS.some(key => permissions[key]) && <button className="home-smm-action" onClick={() => openPortal()}>
          <span className="home-smm-icon">🏢</span><span><strong>Разделы портала</strong><small>Доступные рабочие разделы</small></span><span className="home-action-arrow">›</span>
        </button>}
        </div>

      {permissions['helpdesk.my_tickets'] && (filteredTickets.length === 0 ? (
        <div className="home-empty-state">
          <div className="home-empty-icon">✓</div>
          <h2>{statusFilter === 'closed' ? 'Нет закрытых заявок' : 'Здесь пока пусто'}</h2>
          <p>{statusFilter === 'open' ? 'Новые и текущие заявки появятся в этом разделе.' : 'Выберите другой раздел или создайте заявку.'}</p>
        </div>
      ) : (
        <div className="ticket-list">
          {filteredTickets.map(t => (
            <button type="button" key={t.id} className="ticket-card" onClick={() => navigate(`/app/tickets/${t.id}`)}>
              <div className="ticket-card-header">
                <span className="ticket-number">{t.ticket_number}</span>
                <span className={`status-badge status--${t.status}`}>{STATUS_LABELS[t.status]}</span>
              </div>
              <div className="ticket-card-body">
                <div className="ticket-meta">
                  {PRIORITY_EMOJI[t.priority]} {BLOCK_NAMES[t.block] || t.block}
                </div>
                <div className="ticket-point">{(t.points || [t.point]).join(', ')}</div>
                <div className="ticket-desc">{t.description.slice(0, 80)}{t.description.length > 80 ? '...' : ''}</div>
              </div>
              <div className="ticket-card-footer">
                {new Date(t.created_at).toLocaleDateString('ru-RU')}
              </div>
            </button>
          ))}
        </div>
      ))}
      </main>
    </div>
  );
}
